import * as t from 'io-ts'
import { DateFromISOString, DateFromNumber } from 'io-ts-types'
import { withFallback } from 'io-ts-types/lib/withFallback'

const stringOrUndefined = withFallback<t.StringC | t.UndefinedC>(
  t.string,
  undefined
)

export const Customer = t.type(
  {
    id: t.number,
    address: stringOrUndefined,
    email: withFallback(t.string, ''),
    name: withFallback(t.string, ''),
    phoneNumber: withFallback(t.string, ''),
    taxNumber: stringOrUndefined,
    nymId: stringOrUndefined,
  },
  'Customer'
)
export type Customer = t.TypeOf<typeof Customer>

export const InvoiceStatus = t.keyof({
  UNPAID: null,
  PAID: null,
  PROCESSING: null,
})
export type InvoiceStatus = t.TypeOf<typeof InvoiceStatus>

export const Item = t.type(
  {
    id: t.number,
    name: withFallback(t.string, ''),
    description: withFallback(t.string, ''),
    price: withFallback(t.record(t.string, t.number), {}),
  },
  'Item'
)
export type Item = t.TypeOf<typeof Item>

export const InvoiceItem = t.type(
  {
    quantity: t.number,
    item: Item,
  },
  'InvoiceItem'
)
export type InvoiceItem = t.TypeOf<typeof InvoiceItem>

const orUndefined = <C extends t.Any>(c: C) =>
  withFallback<C | t.UndefinedC>(c, undefined)

export const NewDiscountOrTax = t.type(
  {
    name: t.string,
    rate: t.type(
      {
        numerator: t.number,
        denominator: t.number,
      },
      'Rate'
    ),
    description: orUndefined(t.string),
  },
  'NewDiscountOrTax'
)
export type NewDiscountOrTax = t.TypeOf<typeof NewDiscountOrTax>

export const DiscountOrTax = t.intersection(
  [
    NewDiscountOrTax,
    t.type({
      id: t.number,
      amount: t.number,
    }),
  ],
  'DiscountOrTax'
)
export type DiscountOrTax = t.TypeOf<typeof DiscountOrTax>

export const NewOrganizationInvoice = t.type(
  {
    reference: t.string,
    unitID: t.string,
    amount: t.number,
    createdAt: DateFromISOString,
    dueAt: DateFromISOString,
    customer: Customer,
    items: t.array(InvoiceItem),
    notifyPhonenumber: orUndefined(t.string),
    urlOk: orUndefined(t.string),
    urlFailed: orUndefined(t.string),
    discount: orUndefined(NewDiscountOrTax),
    tax: orUndefined(NewDiscountOrTax),
  },
  'NewOrganizationInvoice'
)
export type NewOrganizationInvoice = t.TypeOf<typeof NewOrganizationInvoice>

export const NewSupplierInvoice = t.type(
  {
    reference: t.string,
    unitID: t.string,
    amount: t.number,
    createdAt: DateFromISOString,
    dueAt: DateFromISOString,
    organization: t.string,
    items: t.array(InvoiceItem),
    email: orUndefined(t.string),
    notifyPhonenumber: orUndefined(t.string),
    urlOk: orUndefined(t.string),
    urlFailed: orUndefined(t.string),
    discount: orUndefined(NewDiscountOrTax),
    tax: orUndefined(NewDiscountOrTax),
  },
  'NewSupplierInvoice'
)
export type NewSupplierInvoice = t.TypeOf<typeof NewSupplierInvoice>

export const InvoiceWithTotal = t.intersection(
  [
    t.type({
      id: t.number,
      reference: t.string,
      unitID: t.string,
      amount: t.number,
      total: t.number,
      createdAt: DateFromNumber,
      dueAt: DateFromNumber,
      status: InvoiceStatus,
      organization: t.string,
      items: t.array(InvoiceItem),
      notifyPhonenumber: orUndefined(t.string),
      urlOk: orUndefined(t.string),
      urlFailed: orUndefined(t.string),
      payer: orUndefined(t.string),
      discount: orUndefined(DiscountOrTax),
      tax: orUndefined(DiscountOrTax),
    }),
    t.union([
      t.type({
        customer: t.undefined,
        supplier: t.string,
      }),
      t.type({
        customer: Customer,
        supplier: t.undefined,
      }),
    ]),
  ],
  'InvoiceWithTotal'
)
export type InvoiceWithTotal = t.TypeOf<typeof InvoiceWithTotal>

export const InvoiceWithOrganization = t.intersection(
  [
    InvoiceWithTotal,
    t.type({
      organizationInfo: t.type(
        {
          name: t.string,
          activity: orUndefined(t.string),
          defaultUnit: orUndefined(t.string),
          nymID: t.string,
        },
        'OrganizationInfo'
      ),
    }),
  ],
  'InvoiceWithOrganization'
)
